import React from 'react';
import { numberToCurrencyFormatter, numFormatterWithSuffix } from './helper';
import {
  Columns,
  cellStyles4,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { getTextWithToolTip } from './riskPatientDetailsHelper';
import { Box, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { benchmarkConstants } from '../messages/benchmarkConstants';
import MedicalHistoryDetails from '../../_components/organisms/MedicalHistoryDetails';
import { dropDownMenuItemsValues } from './tableSortHelper';

export type SummaryData = {
  benchmarked_spend: number;
  avgRatevsCMS: number;
  percent_benchmarked_spend_gt_150_CMS: number;
};
export type CategoryData = {
  total_spend: number;
  benchmarked_spend: number;
  values: number[];
  benchmark_category: string;
  noOfUniqueClaims: number;
  uniqueProcedureCodes: number;
  uniqueMemberIds: number;
  percent_spend: number;
  avgRatevsCMS: number;
  percent_spend_gt_150_CMS: number;
  category: string;
  label: string;
  description: string;
};
export type CategoryList = {
  category_name: string;
  category_key: string;
};
export type CategoryDetails = {
  total_spend: number;
  benchmarked_spend: number;
  procedure_desc: string;
  procedure_code: string;
  avgRatevsCMS: number;
  claimIds: string[];
  noOfClaims: number;
  total_rate: number;
  total_units: number;
  totalSpendPerUnit: number;
  benchmarkedSpendPerUnit: number;
  riskPatientDetails: any[];
};
export type TextWithInfoIconProps = {
  text: string;
  tooltipText: string;
};
export type CategoryDetailsSummary = {
  noOfMemberIds: number;
  member_unique_ids: string[];
};

const abbrevMap: any = {
  MPFS: 'Medicare Physician Fee Schedule',
  ASP: 'Average Sales Price',
  CLFS: 'Clinical Laboratory Fee Schedule',
  OPPS: 'Outpatient Prospective Payment System',
};
const { COLUMNS, INTERNAL_COLUMNS } = benchmarkConstants;

export const expandAbbreviation = (shortForm: string) => {
  if (!shortForm) {
    return '';
  }

  const key = shortForm.trim().toUpperCase();
  const full = abbrevMap[key];

  if (!full) {
    return shortForm;
  }
  return `${full} (${key})`;
};
export const getCategoryName = (
  categoryList: CategoryList[],
  category_key: string,
) => {
  const selectedCategory = categoryList.find(
    (cat) => cat.category_key === category_key,
  );
  return selectedCategory?.category_name;
};
export const formatCardData = (
  value: number,
  type: 'amount' | 'percentage',
) => {
  const formattedValue =
    type === 'amount'
      ? `$${numFormatterWithSuffix(value)}`
      : `${value.toFixed(2)}%`;
  return (
    <Typography
      fontSize="14px"
      textColor={patientAccessTheme.palette.text.lowemphasis}
    >
      <span
        style={{
          fontSize: '20px',
          fontWeight: 500,
          lineHeight: '43px',
          color: patientAccessTheme.palette.text.highemphasis,
        }}
      >
        {formattedValue}
      </span>
    </Typography>
  );
};
export const TextWithInfoIcon: React.FC<TextWithInfoIconProps> = ({
  text,
  tooltipText,
}) => {
  return (
    <Box style={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        fontSize="12px"
        textColor={patientAccessTheme.palette.status.white}
        lineHeight="20px"
        fontType="medium"
      >
        {text}
      </Typography>

      <Tooltip
        disableFocusListener
        placement="bottom"
        title={<Typography fontSize="12px">{tooltipText}</Typography>}
        arrow
      >
        <InfoOutlinedIcon
          style={{
            width: '17px',
            height: '17px',
            marginLeft: '4px',
            fill: patientAccessTheme.palette.status.icon,
            cursor: 'pointer',
          }}
        />
      </Tooltip>
    </Box>
  );
};
export const getBenchmarkCategoriesColumn = (): Columns[] => {
  const columns: Columns[] = [
    {
      name: 'Category',
      align: 'left',
      width: '330px',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
        lineHeight: '24px',
      },
    },
    {
      name: 'CMS Benchmark Label',
      align: 'left',
      width: '170px',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
        lineHeight: '24px',
      },
      showEllipsis: true,
      ellipsisWidth: '160px',
    },
    {
      name: (
        <TextWithInfoIcon
          text={COLUMNS.BENCHMARK_SPEND.label}
          tooltipText={COLUMNS.BENCHMARK_SPEND.description}
        />
      ),
      align: 'left',
      width: '150px',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
    {
      name: (
        <TextWithInfoIcon
          text={COLUMNS.BENCHMARK_CLAIM_COUNT.label}
          tooltipText={COLUMNS.BENCHMARK_CLAIM_COUNT.description}
        />
      ),
      align: 'left',
      width: '150px',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
    {
      name: (
        <TextWithInfoIcon
          text={COLUMNS.AVG_RATE_VS_CMS_WEIGHTED.label}
          tooltipText={COLUMNS.AVG_RATE_VS_CMS_WEIGHTED.description}
        />
      ),
      align: 'left',
      width: '150px',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
    {
      name: (
        <TextWithInfoIcon
          text={COLUMNS.PERCENT_SPEND_OVER_150_CMS.label}
          tooltipText={COLUMNS.PERCENT_SPEND_OVER_150_CMS.description}
        />
      ),
      align: 'left',
      width: '150px',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
  ];
  return columns;
};
export const getBenchmarkCategoryColumn = (): Columns[] => {
  const columns: Columns[] = [
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.PROCEDURE_DRUG_CODE.label}
          tooltipText={INTERNAL_COLUMNS.PROCEDURE_DRUG_CODE.description}
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.PROCEDURE_DRUG_DESCRIPTION.label}
          tooltipText={INTERNAL_COLUMNS.PROCEDURE_DRUG_DESCRIPTION.description}
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.CLAIM_COUNT.label}
          tooltipText={INTERNAL_COLUMNS.CLAIM_COUNT.description}
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
      showThisColumn: true,
      sortColumnName: 'noOfClaims',
      DropDownMenuItems: dropDownMenuItemsValues,
    },
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.TOTAL_UNITS.label}
          tooltipText={INTERNAL_COLUMNS.TOTAL_UNITS.description}
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
      showThisColumn: true,
      sortColumnName: 'total_units',
      DropDownMenuItems: dropDownMenuItemsValues,
    },
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.PAID_AMOUNT.label}
          tooltipText={INTERNAL_COLUMNS.PAID_AMOUNT.description}
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
      showThisColumn: true,
      sortColumnName: 'total_spend',
      DropDownMenuItems: dropDownMenuItemsValues,
    },
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.PAID_AMOUNT_PER_UNIT.label}
          tooltipText={INTERNAL_COLUMNS.PAID_AMOUNT_PER_UNIT.description}
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
      showThisColumn: true,
      sortColumnName: 'totalSpendPerUnit',
      DropDownMenuItems: dropDownMenuItemsValues,
    },
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.CMS_BENCHMARKED_AMOUNT.label}
          tooltipText={INTERNAL_COLUMNS.CMS_BENCHMARKED_AMOUNT.description}
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
      showThisColumn: true,
      sortColumnName: 'total_rate',
      DropDownMenuItems: dropDownMenuItemsValues,
    },
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.CMS_BENCHMARKED_AMOUNT_PER_UNIT.label}
          tooltipText={
            INTERNAL_COLUMNS.CMS_BENCHMARKED_AMOUNT_PER_UNIT.description
          }
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
      showThisColumn: true,
      sortColumnName: 'benchmarkedSpendPerUnit',
      DropDownMenuItems: dropDownMenuItemsValues,
    },
    {
      name: (
        <TextWithInfoIcon
          text={INTERNAL_COLUMNS.AVG_RATE_VS_CMS.label}
          tooltipText={INTERNAL_COLUMNS.AVG_RATE_VS_CMS.description}
        />
      ),
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
      showThisColumn: true,
      sortColumnName: 'avgRatevsCMS',
      DropDownMenuItems: dropDownMenuItemsValues,
    },
  ];
  return columns;
};
export const formatCategoryData = (
  categoryData: CategoryData[],
  handleCategoryClick: (
     
    category_key: string,
     
    categoryDescription: string,
  ) => void,
) => {
  const data: any[] = [];

  categoryData.map((category) => {
    const rowData = [];
    rowData.push(
      <Box style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Typography
          fontSize="14px"
          fontType="medium"
          textColor={patientAccessTheme.palette.primary.main}
          styleProps={{
            textDecoration: 'underline dashed',
            textUnderlineOffset: '6px',
            cursor: 'pointer',
          }}
          onClick={() =>
            handleCategoryClick(
              category.benchmark_category,
              category.description,
            )
          }
        >
          {category.category}
        </Typography>
        <Tooltip
          disableFocusListener
          placement="bottom"
          title={
            <Typography fontSize="12px">{category.description}</Typography>
          }
          arrow
        >
          <InfoOutlinedIcon
            style={{
              width: '17px',
              height: '17px',
              fill: patientAccessTheme.palette.status.icon,
              cursor: 'pointer',
            }}
          />
        </Tooltip>
      </Box>,
    );
    rowData.push(category.label);
    rowData.push(numberToCurrencyFormatter(category.benchmarked_spend));
    rowData.push(category.noOfUniqueClaims);
    rowData.push(category.avgRatevsCMS.toFixed(2) + '%');
    rowData.push(category.percent_spend_gt_150_CMS.toFixed(2) + '%');
    data.push(rowData);
  });
  return data;
};
export const generateCategoryList = (categoryData: CategoryData[]) => {
  const data: any[] = [];
  categoryData.map((category) =>
    data.push({
      category_name: category.category,
      category_key: category.benchmark_category,
    }),
  );
  return data;
};
export const formatCategoryDetails = (categoryDetails: CategoryDetails[]) => {
  const data: any[] = [];

  categoryDetails.map((cat) => {
    const rowData = [];
    rowData.push(cat.procedure_code);
    rowData.push(getTextWithToolTip(cat.procedure_desc, 40));
    rowData.push(cat.noOfClaims);
    rowData.push(cat.total_units || '-');
    rowData.push(numberToCurrencyFormatter(cat.total_spend));
    rowData.push(
      cat.totalSpendPerUnit
        ? numberToCurrencyFormatter(cat.totalSpendPerUnit)
        : '-',
    );
    rowData.push(numberToCurrencyFormatter(cat.total_rate));
    rowData.push(
      cat.benchmarkedSpendPerUnit
        ? numberToCurrencyFormatter(cat.benchmarkedSpendPerUnit)
        : '-',
    );
    rowData.push(cat.avgRatevsCMS.toFixed(2) + '%');
    rowData.push(
      <MedicalHistoryDetails
        riskPatientDetails={cat.riskPatientDetails}
        isBenchmarkPage={true}
      />,
    );
    data.push(rowData);
  });
  return data;
};

const quantile = (sorted: number[], q: number) => {
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;

  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
};
const computeBoxPlotStats = (values: number[]) => {
  if (!values || values.length === 0) {
    return null;
  }

  const sorted = values
    .filter((v) => typeof v === 'number' && !isNaN(v))
    .sort((a, b) => a - b);

  if (sorted.length === 0) {
    return null;
  }

  const q1 = quantile(sorted, 0.25);
  const median = quantile(sorted, 0.5);
  const q3 = quantile(sorted, 0.75);

  const iqr = q3 - q1;

  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;

  const min = sorted[0];
  const max = sorted[sorted.length - 1];

  const outliers = sorted.filter((v) => v < lowerFence || v > upperFence);

  return {
    min,
    q1,
    median,
    q3,
    max,
    outliers,
  };
};

export const generateChartData = (categoryData: CategoryData[]) => {
  return categoryData
    .map((cat) => {
      const stats = computeBoxPlotStats(cat.values);

      if (!stats) {
        return null;
      }
      const { min, q1, median, q3, max, outliers } = stats;
      return {
        min: min.toString(),
        q1: q1.toString(),
        median: median.toString(),
        q3: q3.toString(),
        max: max.toString(),
        outliers: outliers.join(','),
      };
    })
    .filter(Boolean);
};
export const generateXAxisLables = (categoryData: CategoryData[]) => {
  return categoryData.map((cat) => {
    return { label: expandAbbreviation(cat.benchmark_category) };
  });
};
